/**
 * Phase 2L-A16 — Dashboard primitives.
 *
 * Small, reusable building blocks for the two new dashboards. Kept in one
 * file so we don't litter /components with single-use one-liners.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { formatDateTimeAU } from "@/lib/formatDateAU";

export function DashboardKpi({ label, value, hint, tone = "default", testId, to, icon: Icon }) {
  // Phase 2L-A94 — defensive value rendering. React renders nothing
  // for `undefined`/`null` which leaves the big-number area visibly
  // blank. Normalize to "0" so KPI tiles always carry a value. Strings
  // (e.g. formatted currency like "$1,234.00") pass through unchanged.
  let displayValue = value;
  if (value === null || value === undefined) {
    displayValue = "0";
  } else if (typeof value === "number" && !Number.isFinite(value)) {
    displayValue = "0";
  }
  const tones = {
    default: "border-slate-200 hover:border-cyan-500",
    success: "border-emerald-200 hover:border-emerald-500 bg-emerald-50/30",
    warn: "border-amber-200 hover:border-amber-500 bg-amber-50/30",
    danger: "border-rose-200 hover:border-rose-500 bg-rose-50/30",
  };
  const inner = (
    <div className={`relative rounded-2xl bg-white border ${tones[tone]} p-5 transition-all`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-slate-500" /> : null}
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-600">{label}</div>
        </div>
        {to ? <ArrowRight className="w-4 h-4 text-slate-400" /> : null}
      </div>
      <div data-testid={testId} className="text-3xl font-display font-bold text-slate-900 leading-none">{displayValue}</div>
      {hint ? <div className="text-xs text-slate-500 mt-2">{hint}</div> : null}
    </div>
  );
  if (to) return <Link to={to} className="group block">{inner}</Link>;
  return inner;
}

export function DashboardSection({ title, subtitle, children, action, testId }) {
  return (
    <section data-testid={testId} className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function EmptyState({ label = "No data yet", className = "" }) {
  return <div className={`text-sm text-slate-400 italic py-6 text-center ${className}`}>{label}</div>;
}

export function LoadingSkeleton() {
  return (
    <div className="flex items-center gap-2 text-slate-400 text-sm py-6 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading dashboard…
    </div>
  );
}

export function FunnelBar({ stages, totalLabel = "in pipeline" }) {
  const total = stages.reduce((acc, s) => acc + (s.count || 0), 0);
  if (!total) return <EmptyState />;
  const palette = ["bg-slate-400","bg-cyan-500","bg-cyan-600","bg-indigo-600","bg-violet-600","bg-emerald-600"];
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
        {stages.map((s, i) => {
          const w = total ? `${(s.count / total) * 100}%` : "0%";
          return <div key={s.stage} className={palette[i % palette.length]} style={{ width: w }} title={`${s.stage}: ${s.count}`} />;
        })}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        {stages.map((s, i) => (
          <div key={s.stage} className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${palette[i % palette.length]}`} />
            <span className="text-slate-700 capitalize">{s.stage.replaceAll("_", " ")}</span>
            <span className="ml-auto font-semibold text-slate-900">{s.count}</span>
          </div>
        ))}
      </div>
      <div className="text-[11px] text-slate-400 mt-2">{total} {totalLabel}</div>
    </div>
  );
}

export function MiniBarRow({ rows, labelKey, valueKey, max }) {
  if (!rows?.length) return <EmptyState />;
  const peak = max || rows.reduce((m, r) => Math.max(m, r[valueKey] || 0), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={`${r[labelKey]}-${i}`} className="flex items-center gap-3 text-xs">
          <div className="w-32 truncate text-slate-700">{r[labelKey] || "—"}</div>
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-cyan-500" style={{ width: `${(r[valueKey] / peak) * 100}%` }} />
          </div>
          <div className="w-10 text-right font-semibold text-slate-900">{r[valueKey]}</div>
        </div>
      ))}
    </div>
  );
}

export function AlertRow({ title, count, refs, items, hint, tone = "warn", to }) {
  if (!count) return null;
  const tones = {
    warn: "border-amber-200 bg-amber-50/40",
    danger: "border-rose-200 bg-rose-50/40",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className={`text-xs font-bold px-2 py-0.5 rounded-full ${tone === "danger" ? "bg-rose-600 text-white" : "bg-amber-600 text-white"}`}>{count}</div>
      </div>
      {hint ? <p className="text-xs text-slate-600 mb-2">{hint}</p> : null}
      {Array.isArray(refs) && refs.length ? (
        <div className="flex flex-wrap gap-1.5">
          {refs.map((r) => <span key={r} className="text-[11px] font-mono px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-700">{r}</span>)}
        </div>
      ) : null}
      {Array.isArray(items) && items.length ? (
        <ul className="space-y-1 text-xs text-slate-700">
          {items.map((it, i) => <li key={i} className="truncate">{it.subject || it.name || it.reference || JSON.stringify(it)}</li>)}
        </ul>
      ) : null}
      {to ? <Link to={to} className="text-xs font-semibold text-amber-700 mt-2 inline-flex items-center gap-1">Investigate <ArrowRight className="w-3 h-3" /></Link> : null}
    </div>
  );
}

export function ActivityFeed({ events }) {
  if (!events?.length) return <EmptyState label="No recent activity" />;
  return (
    <ul className="divide-y divide-slate-100">
      {events.map((e, i) => (
        <li key={i} className="flex items-start justify-between gap-3 py-2 text-xs">
          <div className="min-w-0">
            <div className="font-mono text-[11px] text-slate-500">{formatDateTimeAU(e.ts)}</div>
            <div className="text-slate-800 truncate">{e.event} {e.ref ? <span className="text-slate-400">· {e.ref}</span> : null}</div>
          </div>
          <div className="text-slate-500 text-[11px] whitespace-nowrap">{e.actor || ""}</div>
        </li>
      ))}
    </ul>
  );
}

export function formatCents(cents) {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });
}
